/**
 * Document Agent for Ameen Insurance Assistant
 *
 * Handles policy document upload only (identity + vehicle license).
 * Activated after policy agent package selection; then routes to vehicle inspection.
 *
 * TOOLS (from backend):
 * - request_national_id_front, request_national_id_back, request_driving_license
 * - request_vehicle_license_front, request_vehicle_license_back
 * - go_to_under_writing_question_agent: Route to underwriting questions (pass flow_state from context)
 * - go_to_policy_agent, go_to_router_agent, search_faq
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender, UIStateManager } from '../types';
import { noEntryRequirements } from '../guardrails';
import { logger } from '../../utils/logger';
import { handleSearchFAQ } from './RouterAgent';

export function createDocumentAgent(voiceGender: VoiceGender): AgentNode {
  return {
    id: 'document_agent',
    name: 'Document Agent',
    description: 'Handles policy document upload only; then routes to vehicle inspection',

    entryGuardrails: noEntryRequirements(),

    exitGuardrails: {
      transitions: new Map([
        ['go_to_under_writing', {
          target: { agentId: 'under_writing_question_agent' },
          guardrails: []
        }],
        ['go_to_policy', {
          target: { agentId: 'policy_agent' },
          guardrails: []
        }],
        ['go_to_router', {
          target: { agentId: 'router_agent' },
          guardrails: []
        }]
      ]),
      fallback: { agentId: 'router_agent' }
    },

    initialPrompt: 'ابدأ رفع المستندات.',

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext,
      uiState?: UIStateManager
    ): Promise<ToolCallResult> => {
      logger.tool('[DocumentAgent] Tool called:', toolName, args);

      switch (toolName) {
        case 'go_to_router_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Returning to menu' },
            triggersTransition: true,
            transitionTo: 'router_agent',
            transitionData: { return_to_menu: true },
          };

        case 'go_to_policy_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Returning to policy flow' },
            triggersTransition: true,
            transitionTo: 'policy_agent',
            transitionData: {
              flow_state: context.transitionData.flow_state
            }
          };

        case 'go_to_under_writing_question_agent':
          logger.info('[DocumentAgent] Transitioning to under_writing_question_agent');
          const flowState = args.flow_state ?? context.transitionData.flow_state ?? {};
          return {
            toolName,
            args,
            result: { success: true, message: 'Starting underwriting questions' },
            triggersTransition: true,
            transitionTo: 'under_writing_question_agent',
            transitionData: {
              caller_agent: 'policy_agent',
              flow_state: flowState
            }
          };

        case 'request_national_id_front':
          if (uiState) uiState.setFlowStep('national_id_front');
          return {
            toolName,
            args,
            result: {
              success: true,
              message: 'Upload UI displayed.'
            }
          };

        case 'request_national_id_back':
          if (uiState) uiState.setFlowStep('national_id_back');
          return {
            toolName,
            args,
            result: {
              success: true,
              message: 'Upload UI displayed.'
            }
          };

        case 'request_driving_license':
          if (uiState) uiState.setFlowStep('driving_license');
          return {
            toolName,
            args,
            result: {
              success: true,
              message: 'Upload UI displayed.'
            }
          };

        case 'request_vehicle_license_front':
          if (uiState) uiState.setFlowStep('vehicle_license_front');
          return {
            toolName,
            args,
            result: {
              success: true,
              message: 'Upload UI displayed.'
            }
          };

        case 'request_vehicle_license_back':
          if (uiState) uiState.setFlowStep('vehicle_license_back');
          return {
            toolName,
            args,
            result: {
              success: true,
              message: 'Upload UI displayed.'
            }
          };

        case 'search_faq':
          return await handleSearchFAQ(args, toolName);

        default:
          logger.warn('[DocumentAgent] Unknown tool:', toolName);
          return {
            toolName,
            args,
            result: { success: false, error: `Unknown tool: ${toolName}` }
          };
      }
    },

    onActivate: async (context: AgentContext) => {
      logger.info('[DocumentAgent] Activated');
      try {
        const flowState = (context.transitionData.flow_state ?? {}) as Record<string, any>;
        const vehicleMake = flowState.vehicleMake;
        const vehicleModel = flowState.vehicleModel;
        if (vehicleMake || vehicleModel) {
          const existing = sessionStorage.getItem('saaed_user_data');
          const userData = existing ? JSON.parse(existing) : {};
          userData.vehicleMakeStated = vehicleMake || '';
          userData.vehicleModelStated = vehicleModel || '';
          sessionStorage.setItem('saaed_user_data', JSON.stringify(userData));
          logger.info('[DocumentAgent] Saved vehicleMake/vehicleModel to sessionStorage for license comparison');
        }
      } catch (e) {
        logger.warn('[DocumentAgent] Failed to save vehicleMake/vehicleModel to sessionStorage', e);
      }
    },

    onDeactivate: async (context: AgentContext) => {
      logger.info('[DocumentAgent] Deactivated');
    }
  };
}
